import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { theme } from './styles/theme';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Orders from './pages/Orders';
import Vendors from './pages/Vendors';
import Accounting from './pages/Accounting';
import Settings from './pages/Settings';
import api from './services/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Loading component
const LoadingSpinner = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box p={3} textAlign="center">
          <h2>Something went wrong.</h2>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </Box>
      );
    }

    return this.props.children;
  }
}

const App: React.FC = () => {
  console.log('App rendering'); // Debug log
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <Suspense fallback={<LoadingSpinner />}>
              <Router>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  
                  {/* Protected routes with role-based access */}
                  <Route 
                    path="/" 
                    element={
                      <ProtectedRoute requiredPermission="dashboard.view">
                        <MainLayout><Dashboard /></MainLayout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute requiredPermission="dashboard.view">
                        <MainLayout><Dashboard /></MainLayout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/products" 
                    element={
                      <ProtectedRoute requiredPermission="products.view">
                        <MainLayout><Products /></MainLayout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/orders" 
                    element={
                      <ProtectedRoute requiredPermission="orders.view">
                        <MainLayout><Orders /></MainLayout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/vendors" 
                    element={
                      <ProtectedRoute requiredPermission="vendors.view">
                        <MainLayout><Vendors /></MainLayout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/accounting" 
                    element={
                      <ProtectedRoute requiredPermission="accounting.view">
                        <MainLayout><Accounting /></MainLayout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/settings" 
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <MainLayout><Settings /></MainLayout>
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Fallback route */}
                  <Route 
                    path="*" 
                    element={
                      <ProtectedRoute requiredPermission="dashboard.view">
                        <MainLayout><Dashboard /></MainLayout>
                      </ProtectedRoute>
                    } 
                  />
                </Routes>
              </Router>
            </Suspense>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;